// -----------------------------------------------------------------------------
// Shared sorting helper for ACH-Hire tables (Sep 2026).
//
// Written after Todd asked for A–Z on the Hires table and we built it for that
// one page — then Michael went looking for it on Machines and Customers and
// couldn't find it. Sorting belongs to "how a list behaves", not to the Hires
// page specifically. So this is now one implementation, imported by every
// table that renders things Todd needs to find.
//
// Interaction contract (must match across every page that uses this):
//   - First click on a column header: sort ASC, show `▲`.
//   - Second click on the same column: flip to DESC, show `▼`.
//   - Click a different column: sort ASC on that column, arrow moves.
//   - Empties (null / undefined / "") always sort to the end regardless of
//     direction, so a "no due-back date yet" row doesn't wander into the
//     middle of a date-sorted range.
//
// The per-column value getter is a callback because different pages
// pull the value from different shapes (Hires reads h.machine.unit_number,
// Machines reads m.unit_number directly, etc.). Callers pass whatever
// makes their row model work; comparisons happen here.
// -----------------------------------------------------------------------------
import { useState } from "react";

export function useSortable(initial = { key: null, dir: "asc" }) {
  const [sort, setSort] = useState(initial);
  const toggleSort = (key) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "asc" });
  };
  const sortArrow = (key) => sort.key !== key ? "" : (sort.dir === "asc" ? " ▲" : " ▼");
  const doSort = (rows, getValue) => applySort(rows, sort, getValue);
  return { sort, toggleSort, sortArrow, applySort: doSort };
}

export function applySort(rows, sort, getValue) {
  if (!sort.key) return rows;
  const dirMul = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = getValue(a, sort.key);
    const vb = getValue(b, sort.key);
    const aEmpty = va === null || va === undefined || va === "";
    const bEmpty = vb === null || vb === undefined || vb === "";
    if (aEmpty && !bEmpty) return 1;
    if (!aEmpty && bEmpty) return -1;
    if (aEmpty && bEmpty) return 0;
    if (va < vb) return -1 * dirMul;
    if (va > vb) return  1 * dirMul;
    return 0;
  });
}

// Unit-number key: ACH102 must sort before ACH514, and if a unit number
// ever isn't the neat "ACH" + digits pattern (import quirks, one-off
// assets), it drops back to lowercase-string ordering so it doesn't
// crash out or wander somewhere absurd.
//
// Strategy: separate leading letters from a trailing integer, then
// zero-pad the integer to a wide fixed width. That way string
// comparison alone gives the right numeric order and stays consistent
// with everything else — no mixed number/string comparison inside the
// sort comparator.
export function unitNumberKey(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const m = s.match(/^([A-Za-z]+)(\d+)$/);
  return m ? m[1].toLowerCase() + m[2].padStart(8, "0") : s.toLowerCase();
}
